import React, { useState } from "react";
import IconHelpLight from "@/icons/IconHelpLight";

interface Example {
  label: string;
  input: string;
  output: string;
}

interface VariableHelpCardProps {
  context: "sendMessage" | "httpRequest" | "sendEmail" | "setData" | "askQuestion";
}

const CONTEXT_HELP: Record<
  string,
  { title: string; tip: string; examples: Example[] }
> = {
  sendMessage: {
    title: "Using Variables in Messages",
    tip: "Variables let you personalize messages with visitor information. Data collected from the prechat form (name, email) is automatically available.",
    examples: [
      {
        label: "Greet by name",
        input: "Hi <<name>>, how can I help you today?",
        output: "Hi Sarah Johnson, how can I help you today?"
      },
      {
        label: "Use prechat email",
        input: "I see your email is <<email>>. Let me look up your account.",
        output: "I see your email is sarah@example.com. Let me look up your account."
      },
      {
        label: "Reference saved data",
        input: "Your ticket number is <<ticketId>>. We'll follow up soon!",
        output: "Your ticket number is TK-4821. We'll follow up soon!"
      }
    ]
  },
  httpRequest: {
    title: "Using Variables in HTTP Requests",
    tip: "Pass visitor data to your CRM, ticketing system, or any API. Variables work in the URL, headers, and body.",
    examples: [
      {
        label: "Look up customer by email",
        input: "https://api.yourcrm.com/customers?email=<<email>>",
        output: "https://api.yourcrm.com/customers?email=sarah@example.com"
      },
      {
        label: "Send data in JSON body",
        input: '{ "name": "<<name>>", "issue": "<<issueType>>" }',
        output: '{ "name": "Sarah Johnson", "issue": "billing" }'
      },
      {
        label: "Custom header with account ID",
        input: "X-Account-Id: <<accountId>>",
        output: "X-Account-Id: ACC-1234"
      }
    ]
  },
  sendEmail: {
    title: "Using Variables in Emails",
    tip: "Send personalized emails to visitors or your team. The visitor's email from prechat is available as <<email>>.",
    examples: [
      {
        label: "Email the visitor directly",
        input: "To: <<email>>",
        output: "To: sarah@example.com"
      },
      {
        label: "Personalized subject line",
        input: "Re: Your support request, <<firstName>>",
        output: "Re: Your support request, Sarah"
      },
      {
        label: "Include collected info in body",
        input: "Hello <<name>>,\n\nYour order #<<orderId>> has been updated.",
        output: "Hello Sarah Johnson,\n\nYour order #ORD-5678 has been updated."
      }
    ]
  },
  setData: {
    title: "Saving Data with Variables",
    tip: "Use Set Data to store information for later use. You can combine text with existing variables to create new values.",
    examples: [
      {
        label: "Store a static value",
        input: 'Name: "priority"  Value: "high"',
        output: 'Saves priority = "high" for use as <<priority>>'
      },
      {
        label: "Copy prechat data",
        input: 'Name: "customerEmail"  Value: "<<email>>"',
        output: 'Saves customerEmail = "sarah@example.com"'
      },
      {
        label: "Build a greeting from parts",
        input: 'Name: "greeting"  Value: "Welcome back, <<firstName>>!"',
        output: 'Saves greeting = "Welcome back, Sarah!"'
      }
    ]
  },
  askQuestion: {
    title: "Using Variables in Questions",
    tip: "Personalize your questions with visitor data. The visitor's answer is saved to the variable name you choose under 'Save Reply As'.",
    examples: [
      {
        label: "Personalized question",
        input: "Hi <<firstName>>, what can I help you with today?",
        output: "Hi Sarah, what can I help you with today?"
      },
      {
        label: "Confirm known info",
        input: "Is <<email>> still your preferred contact email?",
        output: "Is sarah@example.com still your preferred contact email?"
      },
      {
        label: "Follow-up with saved data",
        input: "You mentioned <<issueType>>. Can you tell me more?",
        output: "You mentioned billing. Can you tell me more?"
      }
    ]
  }
};

export default function VariableHelpCard({ context }: VariableHelpCardProps) {
  const [expanded, setExpanded] = useState(false);
  const help = CONTEXT_HELP[context];
  if (!help) return null;

  return (
    <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50/50">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-blue-50 rounded-lg transition-colors"
      >
        <span className="text-blue-500 flex-shrink-0">
          <IconHelpLight />
        </span>
        <span className="text-sm font-medium text-blue-700 flex-1">
          {help.title}
        </span>
        <svg
          className={`w-4 h-4 text-blue-400 transition-transform ${expanded ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {expanded && (
        <div className="px-3 pb-3">
          <p className="text-xs text-slate-600 mb-3 leading-relaxed">
            {help.tip}
          </p>

          <div className="space-y-2">
            {help.examples.map((ex, i) => (
              <div
                key={i}
                className="bg-white rounded-md border border-slate-200 p-2.5"
              >
                <div className="text-xs font-medium text-slate-500 mb-1.5">
                  {ex.label}
                </div>
                <div className="flex flex-col gap-1">
                  <div className="flex items-start gap-1.5">
                    <span className="text-[10px] font-semibold text-slate-400 uppercase mt-0.5 w-10 flex-shrink-0">
                      You type
                    </span>
                    <code className="text-xs font-mono text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded break-all">
                      {ex.input}
                    </code>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <span className="text-[10px] font-semibold text-green-600 uppercase mt-0.5 w-10 flex-shrink-0">
                      Result
                    </span>
                    <code className="text-xs font-mono text-green-700 bg-green-50 px-1.5 py-0.5 rounded break-all">
                      {ex.output}
                    </code>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* How data flows from prechat */}
          <div className="mt-3 bg-slate-50 rounded-md border border-slate-200 p-2.5">
            <div className="text-xs font-medium text-slate-500 mb-1">
              Where does the data come from?
            </div>
            <div className="text-[11px] text-slate-500 leading-relaxed space-y-1">
              <p>
                <strong className="text-slate-700">Prechat form</strong> — Name
                and email entered before chat starts become{" "}
                <code className="bg-slate-200 px-1 rounded font-mono">
                  {"<<name>>"}
                </code>{" "}
                and{" "}
                <code className="bg-slate-200 px-1 rounded font-mono">
                  {"<<email>>"}
                </code>
              </p>
              <p>
                <strong className="text-slate-700">Ask Question</strong> — When
                you save a reply as &quot;issueType&quot;, it becomes{" "}
                <code className="bg-slate-200 px-1 rounded font-mono">
                  {"<<issueType>>"}
                </code>
              </p>
              <p>
                <strong className="text-slate-700">Set Data</strong> — Values
                you set (like &quot;priority&quot; = &quot;high&quot;) become{" "}
                <code className="bg-slate-200 px-1 rounded font-mono">
                  {"<<priority>>"}
                </code>
              </p>
              <p>
                <strong className="text-slate-700">Custom Fields</strong> —
                Fields you create in Settings &gt; Custom Fields
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
